import { useEffect } from 'react';

import {
  formatFamilyParentLabel,
  useGetFamilyByStudent,
} from '~entities/family';

import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

type PayingParentSelectProps = {
  studentId: string;
  value?: string;
  onChange: (payingParentId: string) => void;
  disabled?: boolean;
  label?: string;
  compact?: boolean;
};

export const PayingParentSelect = ({
  studentId,
  value,
  onChange,
  disabled,
  label = 'Paying parent',
  compact = false,
}: PayingParentSelectProps) => {
  const { data, isLoading } = useGetFamilyByStudent(studentId);
  const family = data?.family ?? null;
  const contacts = data?.contacts ?? [];
  const billingParentId = family?.billingParentId;
  const fallbackParentId = data?.student?.parentId;

  useEffect(() => {
    if (value) return;
    const defaultId = billingParentId || fallbackParentId;
    if (defaultId) onChange(defaultId);
    // Default once family loads; parent owns subsequent changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- avoid onChange identity loops
  }, [value, billingParentId, fallbackParentId]);

  if (isLoading) {
    return (
      <p className='text-sm text-muted-foreground'>Loading family contacts…</p>
    );
  }

  if (!family || contacts.length === 0) {
    return null;
  }

  const selected = value || billingParentId || fallbackParentId || '';

  return (
    <div className={compact ? 'grid gap-1' : 'grid gap-2'}>
      {!compact && <Label>{label}</Label>}
      {compact && (
        <span className='text-xs text-muted-foreground'>{label}</span>
      )}
      <Select
        value={selected}
        onValueChange={onChange}
        disabled={disabled || contacts.length < 2}
      >
        <SelectTrigger>
          <SelectValue placeholder='Select paying parent' />
        </SelectTrigger>
        <SelectContent>
          {contacts.map((contact) => (
            <SelectItem key={contact.parentId} value={contact.parentId}>
              {formatFamilyParentLabel(contact.parent, contact.parentId)}
              {contact.parentId === billingParentId ? ' (billing)' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
